// frontend/src/utils/dateUtils.js
// Dynamic Date Utilities for UniLedge Platform

/**
 * Get current date and time
 */
export const getCurrentDate = () => {
  return new Date();
};

/**
 * Generate realistic future dates for group deadlines
 * @param {number} daysFromNow - Number of days from today
 * @returns {Date}
 */
export const getFutureDate = (daysFromNow) => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return date;
};

/**
 * Calculate days remaining until a deadline
 * @param {Date} deadline - The deadline date
 * @returns {number} - Days remaining (can be negative if past due)
 */
export const getDaysRemaining = (deadline) => {
  const now = new Date();
  const diffTime = deadline.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

/**
 * Calculate hours remaining until a deadline
 * @param {Date} deadline - The deadline date
 * @returns {number} - Hours remaining
 */
export const getHoursRemaining = (deadline) => {
  const now = new Date();
  const diffTime = deadline.getTime() - now.getTime();
  const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
  return diffHours;
};

/**
 * Format date for display (e.g., "March 15, 2025")
 * @param {Date} date - The date to format
 * @returns {string}
 */
export const formatDisplayDate = (date) => {
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Format date for short display (e.g., "Mar 15")
 * @param {Date} date - The date to format
 * @returns {string}
 */
export const formatShortDate = (date) => {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format time for display (e.g., "2:30 PM")
 * @param {Date} date - The date to format
 * @returns {string}
 */
export const formatTime = (date) => {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format complete date and time (e.g., "March 15, 2025 at 2:30 PM")
 * @param {Date} date - The date to format
 * @returns {string}
 */
export const formatDateTime = (date) => {
  return `${formatDisplayDate(date)} at ${formatTime(date)}`;
};

/**
 * Get countdown text for display
 * @param {Date} deadline - The deadline date
 * @returns {string} - Human readable countdown
 */
export const getCountdownText = (deadline) => {
  const daysLeft = getDaysRemaining(deadline);
  const hoursLeft = getHoursRemaining(deadline);
  
  if (daysLeft < 0) {
    return 'Ended';
  } else if (daysLeft === 0) {
    if (hoursLeft <= 1) {
      return `${hoursLeft} hour left`;
    } else {
      return `${hoursLeft} hours left`;
    }
  } else if (daysLeft === 1) {
    return '1 day left';
  } else {
    return `${daysLeft} days left`;
  }
};

/**
 * Get urgency level for styling
 * @param {Date} deadline - The deadline date
 * @returns {string} - 'urgent', 'warning', or 'normal'
 */
export const getUrgencyLevel = (deadline) => {
  const daysLeft = getDaysRemaining(deadline);
  
  if (daysLeft < 0) {
    return 'ended';
  } else if (daysLeft <= 1) {
    return 'urgent';
  } else if (daysLeft <= 3) {
    return 'warning';
  } else {
    return 'normal';
  }
};

/**
 * Generate realistic pickup/delivery windows
 * @param {number} daysFromDeadline - Days after group deadline
 * @returns {Object} - Pickup window with start and end times
 */
export const generatePickupWindow = (daysFromDeadline = 2) => {
  const deadline = getFutureDate(daysFromDeadline);
  
  // Set pickup to Saturday morning (common for group pickups)
  const pickupDate = new Date(deadline);
  const dayOfWeek = pickupDate.getDay();
  const daysUntilSaturday = (6 - dayOfWeek) % 7;
  pickupDate.setDate(pickupDate.getDate() + daysUntilSaturday);
  
  // Set time to 9 AM - 12 PM window
  const startTime = new Date(pickupDate);
  startTime.setHours(9, 0, 0, 0);
  
  const endTime = new Date(pickupDate);
  endTime.setHours(12, 0, 0, 0);
  
  return {
    date: pickupDate,
    startTime,
    endTime,
    formattedWindow: `${formatShortDate(pickupDate)}, ${formatTime(startTime)}-${formatTime(endTime)}`
  };
};

/**
 * Generate realistic group data with dynamic dates
 * @param {Object} baseGroup - Base group data
 * @returns {Object} - Group with dynamic dates
 */
export const enhanceGroupWithDates = (baseGroup) => {
  const deadline = getFutureDate(baseGroup.staticDaysLeft || 5);
  const pickupWindow = generatePickupWindow(baseGroup.staticDaysLeft + 2);
  
  return {
    ...baseGroup,
    deadline,
    daysLeft: getDaysRemaining(deadline),
    hoursLeft: getHoursRemaining(deadline),
    countdownText: getCountdownText(deadline),
    urgencyLevel: getUrgencyLevel(deadline),
    deliveryDate: formatShortDate(pickupWindow.date),
    pickupWindow: pickupWindow.formattedWindow,
    formattedDeadline: formatDisplayDate(deadline)
  };
};

/**
 * Real-time countdown hook data
 * @param {Date} deadline - The deadline to count down to
 * @returns {Object} - Real-time countdown data
 */
export const getRealtimeCountdown = (deadline) => {
  const now = new Date();
  const timeLeft = deadline.getTime() - now.getTime();
  
  if (timeLeft <= 0) {
    return {
      ended: true,
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      display: 'Ended'
    };
  }
  
  const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
  const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
  
  let display = '';
  if (days > 0) {
    display = `${days}d ${hours}h`;
  } else if (hours > 0) {
    display = `${hours}h ${minutes}m`;
  } else {
    display = `${minutes}m ${seconds}s`;
  }
  
  return {
    ended: false,
    days,
    hours,
    minutes,
    seconds,
    display,
    urgencyLevel: getUrgencyLevel(deadline)
  };
};

/**
 * Format distance to now for message timestamps
 * @param {Date} date - The date to format
 * @returns {string} - Human readable time distance (e.g., "5m ago", "2h ago")
 */
export const formatDistanceToNow = (date) => {
  const seconds = Math.floor((new Date() - date) / 1000);
  
  if (seconds < 60) return 'just now';
  
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;
  
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  
  return `${Math.floor(months / 12)}y ago`;
};